import { create } from 'zustand';
import { DEVTOOLS_OPTIONS } from '@/modules/debug/constants/zustand';
import { devtools } from 'zustand/middleware';
import { Vector2 } from 'three';

export type ContextMenuContext = 'AREA' | 'CONNECTION' | 'CONTROL_POINT' | 'FLOW_STOP' | null;

type contextMenuStoreState = {
  context: ContextMenuContext;
  positionState: null | Vector2;
  relatedComponentIdState: string | undefined;
  relatedControlPointState: { elementId: string; pointIndex: number } | undefined;
  relatedAreaIdsState: string[];
  activeConnectionTabState: 'aToBConnections' | 'bToAConnections' | undefined;
};

type contextMenuActions = {
  getPositionState(): null | Vector2;
  getContextState(): ContextMenuContext;
  setContextState(value: ContextMenuContext): void;
  setPositionState(value: null | Vector2): void;
  getRelatedComponentIdState(): string | undefined;
  setRelatedComponentIdState(value: string | undefined): void;
  getRelatedControlPointState(): { elementId: string; pointIndex: number } | undefined;
  setRelatedControlPointState(value: { elementId: string; pointIndex: number } | undefined): void;
  getRelatedAreaIdsState(): string[];
  setRelatedAreaIdsState(value: string[]): void;
  getActiveConnectionTabState(): 'aToBConnections' | 'bToAConnections' | undefined;
  setActiveConnectionTabState(value: 'aToBConnections' | 'bToAConnections' | undefined): void;
  reset(): void;
};

const INITIAL_STATE = {
  context: null,
  positionState: null,
  relatedComponentIdState: undefined,
  relatedControlPointState: undefined,
  relatedAreaIdsState: [],
  activeConnectionTabState: undefined,
};

export const useContextMenuStore = create<contextMenuStoreState & contextMenuActions>()(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      getContextState() {
        return get().context;
      },

      setContextState(context) {
        set(() => ({ context }), undefined, {
          type: 'setContextState',
        });
      },

      getPositionState() {
        return get().positionState;
      },

      setPositionState(positionState) {
        set(() => ({ positionState }), undefined, {
          type: 'setPositionState',
        });
      },

      getRelatedComponentIdState() {
        return get().relatedComponentIdState;
      },

      setRelatedComponentIdState(relatedComponentIdState) {
        set(() => ({ relatedComponentIdState }), undefined, {
          type: 'setRelatedComponentIdState',
        });
      },

      getRelatedControlPointState() {
        return get().relatedControlPointState;
      },

      setRelatedControlPointState(relatedControlPointState) {
        set(() => ({ relatedControlPointState }), undefined, {
          type: 'setRelatedControlPointState',
        });
      },

      getRelatedAreaIdsState() {
        return get().relatedAreaIdsState;
      },

      setRelatedAreaIdsState(relatedAreaIdsState) {
        set(() => ({ relatedAreaIdsState }), undefined, {
          type: 'setRelatedAreaIdsState',
        });
      },

      getActiveConnectionTabState() {
        return get().activeConnectionTabState;
      },

      setActiveConnectionTabState(activeConnectionTabState) {
        set(() => ({ activeConnectionTabState }), undefined, {
          type: 'setActiveConnectionTabState',
        });
      },

      reset: () => {
        set(INITIAL_STATE, undefined, {
          type: 'reset',
        });
      },
    }),

    { store: 'contextMenuStore', ...DEVTOOLS_OPTIONS },
  ),
);
